import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useAppSelector } from "../reducers/hooks"
import {
  selectPlannerCompanies,
  selectSettings,
} from "../reducers/settingsSlice"
import SettingsApi from "../api/settingsApi"
import { XmppContext } from "../stream/xmppClient"
import { findProfile } from "../reducers/profilesSliceSelectors"
import AvatarImage, { participantPhotoToSrc } from "./AvatarImage"
import { captureError } from "../ErrorHandlers"
import { selectWhatsappRosterItem } from "../reducers/rosterSelectors"
import ClipboardWriter from "../lib/clipboardWriter"
import JID from "@xmpp/jid"
import ProfilePictureUploadModal from "./ProfilePictureUploadModal"
import { syncSelfVcard } from "../stream/keepConnected"
import XmppVCardApi from "../api/xmppVCardApi"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { useTranslation } from "react-i18next"
import MyCompaniesSettings from "@/components/planner/MyCompaniesSettings"
import InterfaceLanguageSelect from "@/components/InterfaceLanguageSelect"
import AdHocCommands, { type RosterSyncStatus } from "@/api/adHocCommands"
import { toast } from "sonner"
import { useUserPermissions } from "@/hooks/useUserPermissions"
import { languages } from "@/lib/languages"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

type ProfileInfoProp = {
  isOpen: boolean
  onClose: () => void
}

const ProfileInfo = ({ isOpen, onClose }: ProfileInfoProp) => {
  const { t } = useTranslation()
  const { client, myJid } = useContext(XmppContext)
  const settings = useAppSelector(selectSettings)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [imgSrc, setImgSrc] = useState<string>("")
  const profile = useAppSelector(findProfile(myJid.bare().toString()))
  const { hasPermission } = useUserPermissions()
  const canSetOwnLanguage =
    hasPermission("users.update_self") || hasPermission("users.update")
  const storedLanguage = profile?.preferredLanguage || "pl"
  // Shown the moment it is picked; re-synced when the account node echoes
  // the saved value back, and reverted if saving fails.
  const [preferredLanguage, setPreferredLanguage] = useState(storedLanguage)
  // Always holds the latest known-good stored value. changePreferredLanguage
  // closes over storedLanguage at call time, so if the stored value changes
  // meanwhile (e.g. pushed from another device) before a failing save's
  // catch runs, a stale closure would revert to the old value instead of
  // the newer one. Reading from this ref instead keeps the revert current.
  const storedLanguageRef = useRef(storedLanguage)
  useEffect(() => {
    setPreferredLanguage(storedLanguage)
    storedLanguageRef.current = storedLanguage
  }, [storedLanguage])
  // Tracks the most recent pick so a slow, failing save can't clobber a
  // newer one: if the user picks A then B before A's request fails, A's
  // catch must not revert past B.
  const latestPickRef = useRef(storedLanguage)

  const changePreferredLanguage = async (language: string) => {
    latestPickRef.current = language
    setPreferredLanguage(language)
    try {
      // Saved as an edit of my own office-user record, which account_config
      // allows with users.update_self. Only the language is sent; the name
      // and phone keep their stored values.
      await AdHocCommands.upsertOfficeUser({
        client,
        username: myJid.local,
        preferredLanguage: language,
      })
    } catch (error) {
      if (latestPickRef.current === language) {
        setPreferredLanguage(storedLanguageRef.current)
      }
      toast.error(t("account.communicationLanguage.saveError"))
      captureError(error, {
        origin: "UserAction",
        extra: { message: "upsertOfficeUser (own language)" },
      })
    }
  }

  const whatsappRosterItem = useAppSelector(selectWhatsappRosterItem)
  const whatsappNumber = useMemo(
    () =>
      whatsappRosterItem && JID.parse(whatsappRosterItem?.jid).local.toString(),
    [whatsappRosterItem],
  )
  const isPlannerRoleEnabled = Boolean(useFeatureFlagEnabled("planner-role"))
  const plannerCompanies = useAppSelector(selectPlannerCompanies)
  const vhost = myJid.domain

  // Widoczność sterowana wyłącznie odpowiedzią serwisu: allowlista siedzi w
  // custom_configs i jest jedynym miejscem konfiguracji. Na vhostach bez
  // skonfigurowanego roster_syncu komenda nie istnieje i pytanie kończy się
  // błędem - wtedy po prostu nie pokazujemy nic.
  const [rosterSync, setRosterSync] = useState<RosterSyncStatus | null>(null)
  const [rosterSyncBusy, setRosterSyncBusy] = useState(false)
  const readyAtRef = useRef(0)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    AdHocCommands.getRosterSyncStatus(client)
      .then((status) => {
        if (!cancelled) {
          setRosterSync(status)
          readyAtRef.current = Date.now() + status.readyIn * 1000
        }
      })
      .catch(() => {
        if (!cancelled) setRosterSync(null)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, client])

  useEffect(() => {
    if (!rosterSync?.allowed) return
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [rosterSync?.allowed])

  const fileInputRef = useRef<HTMLInputElement>(null)

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const onProfilePictureRemove = useCallback(async () => {
    await XmppVCardApi.removeVCardPhoto(client)
    await syncSelfVcard(client)
  }, [client])
  const onWhatsappNumberCopy = useCallback(() => {
    ClipboardWriter.executeAndWriteResult(async () => whatsappNumber)
    window.appToaster.show({
      message: t("account.whatsappNumberCopied"),
      intent: "success",
    })
  }, [whatsappNumber, t])

  const onRosterSyncClick = useCallback(async () => {
    setRosterSyncBusy(true)
    try {
      const result = await AdHocCommands.runRosterSync(client)
      if (result.status === "started") {
        // readyIn comes from the service - the cooldown length is its business,
        // not a number this component keeps a copy of.
        readyAtRef.current = Date.now() + result.readyIn * 1000
        window.appToaster.show({
          message: t("account.rosterSync.started"),
          intent: "success",
        })
      } else if (result.status === "cooldown") {
        readyAtRef.current = Date.now() + result.readyIn * 1000
        window.appToaster.show({
          message: t("account.rosterSync.cooldown", {
            minutes: Math.ceil(result.readyIn / 60),
          }),
          intent: "warning",
        })
      } else if (result.status === "already_running") {
        window.appToaster.show({
          message: t("account.rosterSync.alreadyRunning"),
          intent: "warning",
        })
      } else {
        window.appToaster.show({
          message: t("account.rosterSync.failedWithReason", {
            reason: result.reason,
          }),
          intent: "danger",
        })
      }
    } catch (error) {
      captureError(error, {
        origin: "UserAction",
        extra: { message: "runRosterSync" },
      })
      window.appToaster.show({
        message: t("account.rosterSync.failed"),
        intent: "danger",
      })
    } finally {
      setRosterSyncBusy(false)
    }
  }, [client, t])

  const toggleNotificationsClick = async () => {
    try {
      await SettingsApi.setMobileNotificationsEnabled(
        client,
        !settings.mobileNotificationsEnabled,
      )
    } catch (error) {
      captureError(error, {
        origin: "UserAction",
        extra: { message: "changeNotificationSettings" },
      })
    }
  }

  const toggleNotifyOnlyOnDriverMessagesClick = async () => {
    try {
      await SettingsApi.setNotifyOnlyOnDriverMessages(
        client,
        !settings.notifyOnlyOnDriverMessages,
      )
    } catch (error) {
      captureError(error, {
        origin: "UserAction",
        extra: { message: "changeNotifyOnlyOnDriverMessages" },
      })
    }
  }

  const onSelectFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader()
      reader.addEventListener("load", () => {
        setImgSrc(reader.result?.toString() || "")
        setIsUploadModalOpen(true)
      })
      reader.readAsDataURL(e.target.files[0])
    }
  }, [])

  const toggleShowNicknameClick = async () => {
    try {
      await SettingsApi.setShowNicknameInWhatsapp(
        client,
        !settings.showNicknameInWhatsapp,
      )
    } catch (error) {
      captureError(error, {
        origin: "UserAction",
        extra: { message: "changeNicknameSettings" },
      })
    }
  }

  const accountSettings = (
    <div className="space-y-4">
      {settings.pushSubscriptionPresent && (
        <div className="flex items-center justify-between">
          <Label htmlFor="mobile-notifications" className="text-sm font-normal">
            {t("account.mobileNotifications")}
          </Label>
          <Switch
            id="mobile-notifications"
            checked={settings.mobileNotificationsEnabled}
            onCheckedChange={toggleNotificationsClick}
          />
        </div>
      )}

      {(settings.pushSubscriptionPresent ||
        window.Notification?.permission === "granted") && (
        <div className="flex items-center justify-between">
          <Label htmlFor="notify-only-drivers" className="text-sm font-normal">
            {t("account.notifyOnlyDriverMessages")}
          </Label>
          <Switch
            id="notify-only-drivers"
            checked={settings.notifyOnlyOnDriverMessages}
            onCheckedChange={toggleNotifyOnlyOnDriverMessagesClick}
          />
        </div>
      )}

      <div className="flex items-center justify-between">
        <Label htmlFor="show-name" className="text-sm font-normal">
          {t("account.showNameToDrivers")}
        </Label>
        <Switch
          id="show-name"
          checked={settings.showNicknameInWhatsapp}
          onCheckedChange={toggleShowNicknameClick}
        />
      </div>

      <InterfaceLanguageSelect />

      {/* Hidden without a role that may edit my own record: the save would
          only be rejected with "Missing permission". */}
      {canSetOwnLanguage && (
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor="preferred-language" className="text-sm font-normal">
            {t("account.communicationLanguage.label")}
          </Label>
          <Select
            value={preferredLanguage}
            onValueChange={changePreferredLanguage}
          >
            <SelectTrigger
              id="preferred-language"
              aria-label={t("account.communicationLanguage.label")}
              className="w-44"
            >
              <SelectValue
                placeholder={t("account.communicationLanguage.placeholder")}
              />
            </SelectTrigger>
            <SelectContent>
              {languages.map((language) => (
                <SelectItem key={language.code} value={language.code}>
                  <span className="flex items-center gap-2">
                    <span>{language.flag}</span>
                    <span>{language.name}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {whatsappNumber && (
        <>
          <hr className="m-0 p-0" />
          <div className="flex items-center justify-between">
            <Label className="text-sm text-muted-foreground">
              {t("account.whatsappNumber", { number: whatsappNumber })}
            </Label>
            <Button variant="outline" size="sm" onClick={onWhatsappNumberCopy}>
              {t("account.copy")}
            </Button>
          </div>
        </>
      )}

      {rosterSync?.allowed &&
        (() => {
          const readyIn = Math.max(
            0,
            Math.ceil((readyAtRef.current - now) / 1000),
          )
          return (
            <>
              <hr className="m-0 p-0" />
              <div className="flex items-center justify-between">
                <Label className="text-sm font-normal">
                  {t("account.rosterSync.label")}
                </Label>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={rosterSyncBusy || readyIn > 0}
                  onClick={onRosterSyncClick}
                >
                  {readyIn > 0
                    ? t("account.rosterSync.buttonCooldown", {
                        minutes: Math.ceil(readyIn / 60),
                      })
                    : t("account.rosterSync.button")}
                </Button>
              </div>
            </>
          )
        })()}
    </div>
  )

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          className="max-h-[85vh] overflow-y-auto sm:max-w-md"
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            document.body.style.pointerEvents = ""
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              {t("account.title")}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Profile Picture Section */}
            <div className="flex items-center gap-4">
              <div className="group relative cursor-pointer">
                <label
                  htmlFor="profile-picture-input"
                  className="cursor-pointer"
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={onSelectFile}
                    className="hidden"
                    id="profile-picture-input"
                    ref={fileInputRef}
                  />
                  <AvatarImage
                    name={profile?.name || ""}
                    src={participantPhotoToSrc(profile?.photo)}
                    size="large"
                  />
                  <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black bg-opacity-50 opacity-0 transition-opacity group-hover:opacity-100">
                    <span className="text-xs text-white">
                      {t("account.changePhoto")}
                    </span>
                  </div>
                </label>
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="text-lg font-medium">{profile?.name}</h3>
                <Button
                  variant="link"
                  onClick={onProfilePictureRemove}
                  className="h-auto justify-start p-0 text-xs text-red-700"
                >
                  {t("account.removePhoto")}
                </Button>
              </div>
            </div>

            {/* Settings Section — za flagą planera rozbita na zakładki, żeby
                lista firm dostała całą wysokość modala zamiast doklejać się pod
                przełącznikami. Bez flagi układ jest dokładnie jak wcześniej. */}
            {isPlannerRoleEnabled ? (
              <Tabs defaultValue="account">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="account">
                    {t("account.tabs.account")}
                  </TabsTrigger>
                  <TabsTrigger value="companies">
                    {t("account.tabs.companies")}
                    {plannerCompanies.length > 0 && (
                      <span className="rounded-full bg-foreground/10 px-1.5 py-0.5 text-xs">
                        {plannerCompanies.length}
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="account" className="mt-4">
                  {accountSettings}
                </TabsContent>
                <TabsContent value="companies" className="mt-4">
                  <MyCompaniesSettings client={client} vhost={vhost} />
                </TabsContent>
              </Tabs>
            ) : (
              accountSettings
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ProfilePictureUploadModal
        imgSrc={imgSrc}
        isOpen={isUploadModalOpen}
        onClose={() => {
          setImgSrc("")
          clearFileInput()
          setIsUploadModalOpen(false)
        }}
        client={client}
        myJid={myJid.toString()}
      />
    </>
  )
}

export default ProfileInfo
